import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { mlb, innings, number } from 'yankees-react-utils';
import { NavLink } from 'react-router-dom';
import { useBlobDownloadQuery } from '../data';
import { hitFXERA, pitchFXERA } from '../utils/stats';
import VideoPlayerButton from './VideoPlayerButton';
import usePlayerSettings from '../utils/usePlayerSettings';

const HomeAwayIndicator = styled.h4`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    width: 24px;
    margin-bottom: 0 !important;
    flex-shrink: 0;
`;
const TeamLogo = styled.div`
    background-color: ${({ imgBackground }) => imgBackground};
    object-fit: contain;
    width: 50px;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
`;
const GameLine = styled.div`
    font-size: 13px;
    color: #737373;
    line-height: 1.5;
`;

const gamestatus = ['Scheduled', 'Finished', 'Postponed', 'Canceled', 'Suspended', 'In Progress', 'Not Played'];

const getGameLine = (translate, isHomeGame, game) => {
    if (game.awayscore === null || game.homescore === null) return (<i>{gamestatus[game.gamestatus]}</i>);
    let outcome = '';
    let score = '';

    const homeTeamWins = game.homescore > game.awayscore;
    if (game.awayscore === game.homescore) {
        outcome = translate('baseball.abbr-tie');
    } else {
        outcome = (isHomeGame && homeTeamWins) || (!isHomeGame && !homeTeamWins) ? translate('baseball.abbr-win') : translate('baseball.abbr-loss');
    }

    score = isHomeGame ? `${game.homescore}-${game.awayscore}` : `${game.awayscore}-${game.homescore}`;

    return outcome || score ? `(${outcome} ${score})` : '';
};

const defaultGetStatLines = (translate, game, isPitcher, isCatcher, showGameScore) => {
    const statLines = [];
    let statLine = '';
    if (isPitcher) {
        statLine = '';
        statLine += game.outs !== null ? `${innings(game.outs)} ${translate('baseball.abbr-inning-pitched')}, ` : '';
        statLine += game.so !== null ? `${game.so} ${translate('baseball.abbr-strikeout-k')}, ` : '';
        statLine += game.ubb !== null ? `${game.ubb} ${translate('baseball.abbr-walk')}, ` : '';
        statLine += game.rvgamefx !== null && game.pawrv !== null && showGameScore ? `${translate('sections.reports.pitcher.gamefx-era')}: ${number(hitFXERA(game.rvgamefx, game.pawrv), 2)}, ` : '';
        // Replace the CQ with the PFXERA for Pitchers
        // statLine += game.rvhitfxpitbip !== null && game.biprvdenom !== null
        //     ? `CQ ${number(RVPerN(game.rvhitfxpitbip, game.biprvdenom, 100), 1)}, `
        //     : '';
        // statLine += game.rvpitchfx != null && game.pitchfxwrv != null ? `${translate('home.pfx-era')}: ${number(pitchFXERA(game.rvpitchfx, game.pitchfxwrv), 2)}, ` : '';
        // This is to remove the trailing comma, so make sure any added fields have the ", " or a stat could get truncated
        if (statLine.length >= 2) {
            statLines.push(statLine.substring(0, statLine.length - 2));
        }
        // reset the statLine to drop things down to the next line
        statLine = '';
        statLine += game.rvstuff != null && game.pitchfxwrv != null ? `${translate('home.stuff-era')}: ${number(pitchFXERA(game.rvstuff, game.pitchfxwrv), 2)}, ` : '';
        //  statLine += game.rvbreak != null && game.pitchfxwrv != null ? `${translate('home.break-era')}: ${number(pitchFXERA(game.rvbreak, game.pitchfxwrv), 2)}, ` : '';
        statLine += game.rvexecera != null && game.pwexecera != null ? `${translate('sections.reports.pitcher.exec-era')}: ${number(pitchFXERA(game.rvexecera, game.pwexecera), 2)}, ` : '';

        // rvERA(row.rvexecera);
        if (statLine.length >= 2) {
            statLines.push(statLine.substring(0, statLine.length - 2));
        }
    } else if (isCatcher) {
        statLine = '';
        statLine += game.h2hstk !== null ? `${number(game.h2hstk, 1)} ${translate('home.abbr-h2hxk')}, ` : '';
        statLine += game.tkstk !== null && game.exptkstk !== null ? `${number(game.tkstk - game.exptkstk, 1)} ${translate('home.extra-strikes')}, ` : '';
        if (statLine.length >= 2) {
            statLines.push(statLine.substring(0, statLine.length - 2));
        }
    }
    if (!isPitcher) {
        statLine = [];
        if (game.ab !== null) statLine.push(`${game.b1 + game.b2 + game.b3 + game.hr}-${game.ab}`);
        if (game.ab !== null) statLine.push(`${game.b2 + game.b3 + game.hr} ${translate('home.abbr-extra-base-hits')}`);
        if (game.rbi !== null) statLine.push(`${game.rbi} ${translate('home.abbr-run-batted-in')}`);
        if (game.ubb !== null) statLine.push(`${game.ubb} ${translate('baseball.abbr-walk')}`);
        if (game.so !== null) statLine.push(`${game.so} ${translate('baseball.abbr-strikeout-k')}`);
        if (game.totalgrade !== null && showGameScore) statLine.push(`${translate('sections.post-game.hitter.game-score')}: ${number(game.totalgrade, 1)}`);
        if (statLine.length > 0) {
            statLines.push(statLine.join(', '));
        }
    }

    return statLines;
};

const GameLogGame = ({ playerId, game, isPitcher, isCatcher, wrapper: WrapperProp, getStatLines }) => {
    const { t: translate } = useTranslation();
    const { ShowGameScore } = usePlayerSettings();
    const isHomeGame = game.hometeamid === game.teamid;
    const { src } = useBlobDownloadQuery(`/api/team/${isHomeGame ? game.awayteamid : game.hometeamid}/logo`);
    const Wrapper = ({ children }) => (WrapperProp
        ? <WrapperProp>{children}</WrapperProp>
        : (<NavLink to={`/post-game/${game.gameid}`} className="d-flex">{children}</NavLink>));

    return (
        <div className="d-flex border-bottom align-items-center justify-content-between pe-2 bg-white">
            <Wrapper>
                <HomeAwayIndicator className="bg-dark text-white">{isHomeGame ? translate('baseball.abbr-versus') : '@'}</HomeAwayIndicator>
                <TeamLogo imgBackground={mlb.backgrounds[isHomeGame ? game.awayteamid : game.hometeamid]}>
                    {src && <img src={src} height={28} alt="" />}
                </TeamLogo>
                <div className="p-2 flex-grow-1 text-dark">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-end">
                            <h4 className="mb-0 d-flex align-items-end">
                                {translate('format.datetime', { val: new Date(game.gamedate), interpolation: { escapeValue: false } })}
                                {game.gamenum > 1 && <> (gm {game.gamenum})</>}
                            </h4>
                            <GameLine>&nbsp;{getGameLine(translate, isHomeGame, game)}</GameLine>
                        </div>
                    </div>
                    <div className="text-muted stat-lines">
                        { getStatLines(translate, game, isPitcher, isCatcher, ShowGameScore).map((line, idx) => (
                            // eslint-disable-next-line react/no-array-index-key
                            (<div className="stat-line" key={`${game.gameid}_${idx}`}>{line}</div>)
                        ))}
                    </div>
                </div>
            </Wrapper>
            { game.inbats && (
                <VideoPlayerButton
                    gameid={game.gameid}
                    pitcherid={isPitcher ? playerId : undefined}
                    batterid={isPitcher ? undefined : playerId}
                />
            ) }
        </div>
    );
};

GameLogGame.propTypes = {
    playerId: PropTypes.number.isRequired,
    game: PropTypes.shape({
        gameid: PropTypes.number,
        teamid: PropTypes.number,
        hometeamid: PropTypes.number,
        awayteamid: PropTypes.number,
        gamedate: PropTypes.string,
        haspitchfxdata: PropTypes.bool,
        inbats: PropTypes.bool,
        gamenum: PropTypes.number,
    }).isRequired,
    isPitcher: PropTypes.bool,
    isCatcher: PropTypes.bool,
    wrapper: PropTypes.func,
    getStatLines: PropTypes.func,
};

GameLogGame.defaultProps = {
    isPitcher: false,
    isCatcher: false,
    wrapper: undefined,
    getStatLines: defaultGetStatLines,
};

export default GameLogGame;
